import React, { useContext, useState, useEffect } from 'react';
import { Box, Typography, Container, Button, Stack } from '@mui/material';
import { keyframes } from '@mui/system';
import { useTranslation } from '../../i18n';
import { LangContext } from '../index';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useNavigate } from 'react-router-dom';

const fadeInOut = keyframes`
  0%, 100% { opacity: 0; }
  50% { opacity: 1; }
`;

const fadeOutUp = keyframes`
  0% { 
    opacity: 1;
    transform: translateY(0);
  }
  100% { 
    opacity: 0;
    transform: translateY(-20px);
  }
`;

const buttonHover = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
  100% { transform: translateY(0); }
`;

const zoomIn = keyframes`
  0% {
    opacity: 0.3;
    transform: scale(1.5) translateY(30px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const fadeInButton = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const bounceArrow = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
`;

const buttonGlow = keyframes`
  0% {
    border-color: rgba(255, 255, 255, 0.6);
    box-shadow: 0 0 10px rgba(255, 255, 255, 0.2);
    background-color: rgba(255, 255, 255, 0.1);
  }
  50% {
    border-color: rgba(255, 215, 0, 0.8);
    box-shadow: 0 0 15px rgba(255, 215, 0, 0.4);
    background-color: rgba(255, 215, 0, 0.25);
  }
  100% {
    border-color: rgba(255, 255, 255, 0.6);
    box-shadow: 0 0 10px rgba(255, 255, 255, 0.2);
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const slideUpOut = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(-40px);
  }
`;

const slideUpIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(40px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

interface HeroSectionProps {
  title: string;
  primaryButton?: {
    text: string;
    onClick: () => void;
  };
  secondaryButton?: {
    text: string;
    onClick: () => void;
  };
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  title,
  primaryButton,
  secondaryButton
}) => {
  const { lang } = useContext(LangContext);
  const { t } = useTranslation(lang);
  const navigate = useNavigate();
  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  const words = [
    t('landing.hero.words.videos'),
    t('landing.hero.words.sequences'),
    t('landing.hero.words.compositions'),
    t('landing.hero.words.exercises'),
    t('landing.hero.words.cpa'),
    t('landing.hero.words.games')
  ];
  const [currentWordIndex, setCurrentWordIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWordIndex((prevIndex) => (prevIndex + 1) % words.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [words.length]);

  const handleDiscoverClick = () => {
    navigate('/signup');
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100%',
        position: 'relative',
        scrollSnapAlign: 'start',
        overflow: 'hidden',
        background: isMobileDevice ? '#1a1a1a' : 'transparent'
      }}
    >
      {/* Vidéo en arrière-plan */}
      <Box
        sx={{
          position: 'absolute',
          top: isMobileDevice ? 'calc(50% + 50px)' : '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '100%', md: '60%' },
          aspectRatio: '16/9',
          zIndex: 1,
          '& video': {
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            backgroundColor: 'black'
          }
        }}
      >
        <video
          autoPlay
          loop
          muted
          playsInline
          src="https://uploads-ssl.webflow.com/616d36c01fa209078ee019c1/6670b4bf68a4ae1050ce7b02_Galery1_3%20-%20Copie-transcode.mp4"
          style={{ display: 'block' }}
        />
      </Box>

      {/* Overlay légèrement sombre */}
      {!isMobileDevice && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.2)',
            zIndex: 2
          }}
        />
      )}

      {/* Contenu */}
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 3,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: { xs: 'space-between', md: 'center' },
          alignItems: 'center',
          pt: { xs: '40px', md: 0 },
          pb: { xs: 4, md: 8 }
        }}
      >
        <Typography
          variant="h1"
          component="h1"
          sx={{
            fontWeight: '500',
            fontSize: { xs: '1.8rem', md: '3.5rem' },
            textAlign: 'center',
            color: 'white',
            textShadow: '0 0 6px rgba(0,0,0,0.7)',
            mb: { xs: 0, md: 6 },
            maxWidth: '1000px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: '0.5rem', md: '1rem' },
            mt: { xs: '64px', md: 0 },
            animation: `${zoomIn} 1s cubic-bezier(0.16, 1, 0.3, 1)`,
            '& > *:not(:last-child)': { // Applique l'animation à tous les enfants sauf le dernier (les mots qui changent)
              animation: `${zoomIn} 1s cubic-bezier(0.16, 1, 0.3, 1)`
            }
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            gap: { xs: '0.2rem', md: '0.5rem' }
          }}>
            <span>{t('landing.hero.title.part1')}</span>
            <span>{t('landing.hero.title.part2')}</span>
          </Box>
          <span>{t('landing.hero.title.part3')}</span>
          <Box
            sx={{
              minHeight: { xs: '2.5rem', md: '4.5rem' },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              overflow: 'hidden',
              width: '100%',
              padding: 0
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: 0
              }}
            >
              {/* Mot actuel */}
              <Box
                key={`current-${currentWordIndex}`}
                component="span"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: '#FFD700',
                  fontWeight: 900,
                  fontSize: { xs: '2.2rem', md: '4.2rem' },
                  letterSpacing: '0.05em',
                  position: 'absolute',
                  animation: `${slideUpIn} 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards`,
                  whiteSpace: 'nowrap',
                  width: '100%',
                  left: 0,
                  right: 0,
                  margin: '0 auto',
                  height: '100%'
                }}
              >
                {words[currentWordIndex]}
              </Box>
              {/* Mot précédent qui disparaît */}
              <Box
                key={`prev-${currentWordIndex}`}
                component="span"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: '#FFD700',
                  fontWeight: 900,
                  fontSize: { xs: '2.2rem', md: '4.2rem' },
                  letterSpacing: '0.05em',
                  position: 'absolute',
                  animation: `${slideUpOut} 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards`,
                  whiteSpace: 'nowrap',
                  width: '100%',
                  left: 0,
                  right: 0,
                  margin: '0 auto',
                  height: '100%'
                }}
              >
                {words[(currentWordIndex - 1 + words.length) % words.length]}
              </Box>
            </Box>
          </Box>
        </Typography>

        <Box 
          sx={{ 
            width: '100%',
            mt: { xs: 'auto', md: 4 },
            mb: { xs: '16px', md: 0 }, 
            opacity: 0,
            animation: `${fadeInButton} 1s ease-out forwards`,
            animationDelay: '3s'
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{
              width: '100%',
              justifyContent: 'center'
            }}
          >
            {primaryButton && (
              <Button
                variant="contained"
                size="large"
                onClick={primaryButton.onClick}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  letterSpacing: 1
                }}
              >
                {primaryButton.text}
              </Button>
            )}
            {secondaryButton && (
              <Button
                variant="outlined"
                size="large"
                onClick={handleDiscoverClick}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.9)',
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                    animation: `${buttonHover} 1s ease-in-out infinite`,
                    '& .MuiSvgIcon-root': {
                      transform: 'rotate(45deg)',
                    }
                  },
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  letterSpacing: 1,
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  backdropFilter: 'blur(8px)',
                  transition: 'all 0.3s ease-in-out',
                  animation: `${buttonGlow} 3s ease-in-out infinite`,
                  '& .MuiSvgIcon-root': {
                    transition: 'transform 0.3s ease-in-out',
                  }
                }}
              >
                {t('landing.nav.discover')}
                <RocketLaunchIcon sx={{ fontSize: 20 }} />
              </Button>
            )}
          </Stack>
        </Box>

        {/* Flèche de défilement */}
        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: 16, md: 40 }, 
            left: '50%',
            transform: 'translateX(-50%)',
            opacity: 0,
            animation: `${fadeInButton} 1s ease-out forwards`,
            animationDelay: '7s',
            cursor: 'pointer',
            '&:hover': {
              '& .MuiSvgIcon-root': {
                color: 'primary.main'
              }
            }
          }}
          onClick={() => window.scrollTo({
            top: window.innerHeight,
            behavior: 'smooth'
          })}
        >
          <KeyboardArrowDownIcon 
            sx={{
              fontSize: { xs: 40, md: 48 },
              color: 'white',
              animation: `${bounceArrow} 2s infinite`,
              transition: 'color 0.3s ease-in-out'
            }}
          />
        </Box>
      </Container>
    </Box>
  );
};
